import React, { useState, useEffect } from 'react';
import PageHelp from '../components/common/PageHelp';
import type { HelpKey } from '../components/common/helpContent';
import {
    Box, Paper, Typography, Button, TextField,
    FormControl, InputLabel, Select, MenuItem,
    InputAdornment, Dialog, DialogTitle,
    DialogContent, DialogActions, CircularProgress,
    Alert, Autocomplete, Switch, FormControlLabel, Chip, Stack,
    Table, TableHead, TableRow, TableCell, TableBody, Tooltip, IconButton,
    Collapse, Link, TablePagination, Tabs, Tab
} from '@mui/material';
import {
    Warning, PhoneAndroid, CheckCircle, Info,
    LocationOn, PlayArrow, BusinessCenter,
    FlashOn, PowerSettingsNew, Insights, Refresh, Tune,
    KeyboardArrowDown, KeyboardArrowUp, TravelExplore,
    Campaign, Save
} from '@mui/icons-material';

import { getAllStates, getCitiesByState } from '../services/referenceService';
import {
    bulkActivateDrivers, getSubscriptionPlans,
    getLaunchMode, setLaunchMode, forceOnlineDrivers,
    listDispatchReports, DispatchReport,
    getDispatchPreview, DispatchPreview, OfflineWakeReport,
    getSupplyFunnel, getSupplyGapZones, SupplyFunnel, SupplyGapZones,
    getDriverReachability, DriverReachability,
    explainReach, ReachExplanation,
    getDispatchHistory, DispatchLogRow
} from '../services/cityOperationService';
import { getAppConfigs, updateAppConfig, createAppConfig } from '../services/appConfigService';
import NudgeTemplatesPanel from './cityops/NudgeTemplatesPanel';
import AppAdoptionPanel from './cityops/AppAdoptionPanel';

// Dispatch-tuning AppConfig keys surfaced here (read/written by name; created if missing). These are
// NOT governed (unlike LAUNCH_MODE_CITIES) so they are safe to edit inline. See helpContent cityOpsTuning.
type AppConfigRow = { id: number; key: string; value: string; description?: string };
type TuningKey = { key: string; label: string; defaultVal: string; hint: string; group: string; options?: string[] };
// Grouped (within tabs) so the panel reads as a few labelled sections instead of a flat wall of 13 fields.
const TUNING_KEYS: TuningKey[] = [
    // --- Reach & radius (every ride) ---
    { key: 'NOTIFY_NEARBY_DRIVERS_RADIUS_KM', group: 'Reach & radius', label: 'Dispatch radius (km)', defaultVal: '2', hint: 'How far from the pickup a REAL ride looks for drivers (Tier 1 + Tier 2). Widen (e.g. 2 → 5) for thin markets — this is the right lever, not Tier 3.' },
    { key: 'NOTIFY_NEARBY_DRIVERS_MAX_RESULTS', group: 'Reach & radius', label: 'Dispatch fan-out cap', defaultVal: '100', hint: 'Max nearby drivers a single ride reaches.' },

    // --- Offline wake (off-duty drivers near the pickup) ---
    { key: 'OFFLINE_WAKE_MODE', group: 'Offline wake (off-duty drivers)', label: 'Wake style', defaultVal: 'gentle', options: ['gentle', 'loud'], hint: 'loud = same full-screen ride ring as online drivers (use at launch for max reach). gentle = soft "go online" banner (respects off-duty). The rules below apply to BOTH.' },
    { key: 'OFFLINE_WAKE_RECENCY_MIN', group: 'Offline wake (off-duty drivers)', label: 'Only wake if located within (min)', defaultVal: '60', hint: 'Skip drivers whose last-known location is older than this (they\'ve likely moved). 0 = no recency limit (widest reach).' },
    { key: 'OFFLINE_WAKE_MAX', group: 'Offline wake (off-duty drivers)', label: 'Max wakes before silence', defaultVal: '2', hint: 'If a driver neither goes online nor rejects, stop after this many. Resets when they go online. 0 = no cap (ring freely).' },
    { key: 'OFFLINE_WAKE_WINDOW_HOURS', group: 'Offline wake (off-duty drivers)', label: 'Wake-count reset window (hours)', defaultVal: '24', hint: 'Safety reset for the cap + reject-block, so a dormant driver self-resets after this long.' },
    { key: 'OFFLINE_WAKE_QUIET_START', group: 'Offline wake (off-duty drivers)', label: 'Quiet hours start (IST, 0–23)', defaultVal: '22', hint: 'No offline wakes from here until the end hour. Online drivers still get rings. Set start = end to disable quiet hours.' },
    { key: 'OFFLINE_WAKE_QUIET_END', group: 'Offline wake (off-duty drivers)', label: 'Quiet hours end (IST, 0–23)', defaultVal: '9', hint: 'Offline wakes resume at this hour. Default window 22→9 wraps midnight.' },
    { key: 'OFFLINE_WAKE_STALE_RECENCY_MIN', group: 'Offline wake (off-duty drivers)', label: 'Tier 2.5 stale-nudge ceiling (min)', defaultVal: '0', hint: '0 = off. Near drivers STALER than the recency gate above but within this many minutes get a GENTLE ride-context nudge (recovers the "skipped — stale" drivers). e.g. 1440 = up to 24h.' },

    // --- Demand nudge (broader city, only when near-market is thin) ---
    { key: 'DEMAND_NUDGE_ENABLED', group: 'Demand nudge', label: 'Demand Nudge — ON / OFF', defaultVal: '0', hint: '1 = on, 0 = off. When ON, a real ride that finds few/no near drivers ALSO pings broader offline city drivers to go online (no ride card). Honors quiet hours + "Not now".' },
    { key: 'DEMAND_NUDGE_MIN_NEAR_REACH', group: 'Demand nudge', label: 'Fire when near drivers fewer than', defaultVal: '3', hint: 'Fires only when Tier-1+Tier-2 near-reach is below this. 3 = fire at 0, 1 or 2 near drivers.' },
    { key: 'DEMAND_NUDGE_COOLDOWN_HOURS', group: 'Demand nudge', label: 'Per-driver cooldown (hours)', defaultVal: '3', hint: 'Each driver gets at most ONE demand nudge per this many hours (separate budget from the offline wake above).' },
    { key: 'DEMAND_NUDGE_MAX_RESULTS', group: 'Demand nudge', label: 'Max drivers per nudge', defaultVal: '200', hint: 'Cap on how many offline city drivers a single nudge pings.' },
    { key: 'DEMAND_NUDGE_NEAR_KM', group: 'Demand nudge', label: 'Near-band radius for personalized copy (km)', defaultVal: '4', hint: 'Offline drivers within this many km of the pickup get distance/fare/area copy ("ride ~2km away near Hazratganj, ~Rs 120"); farther drivers get the generic "demand is high" copy.' },

    // --- Advanced ---
    { key: 'CITY_ANCHOR_MIN_REACH', group: 'Advanced', label: 'Tier-3 city-wide ride wake (KEEP 0)', defaultVal: '0', hint: 'KEEP 0. Non-zero rings drivers merely registered to the city (incl. far away) with a real ride offer they often can’t serve. Open the “?” for the full baby-steps explanation.' },

    // --- Re-dispatch waves (#6): retry unaccepted rides at an expanding radius ---
    { key: 'REDISPATCH_ENABLED', group: 'Re-dispatch waves', label: 'Re-dispatch waves — ON / OFF', defaultVal: '0', options: ['0', '1'], hint: '1 = on. If a ride stays unaccepted, ring an EXPANDING radius of NEW drivers over time (never re-ringing anyone already contacted). Cancelled the moment someone accepts.' },
    { key: 'REDISPATCH_WAVE_SECONDS', group: 'Re-dispatch waves', label: 'Wave delays (JSON seconds)', defaultVal: '[45,120,300]', hint: 'JSON array of seconds after the request for each wave, e.g. [45,120,300].' },
    { key: 'REDISPATCH_RADIUS_STEPS_KM', group: 'Re-dispatch waves', label: 'Wave radii (JSON km, decimals OK)', defaultVal: '[2.5,3,3.5]', hint: 'JSON array of km per wave, index-matched to the delays. Decimals allowed, e.g. [2.5,3,3.5]. Keep it tight — drivers 4-5km away rarely accept.' },

    // --- Location ping (#5): silent GPS refresh. NEEDS the new app — keep both ENABLED at 0 until it ships ---
    { key: 'LOCATION_PROBE_ENABLED', group: 'Location ping (probe & sweep)', label: 'JIT probe — ON / OFF', defaultVal: '0', options: ['0', '1'], hint: 'REQUIRES the new app. 1 = at dispatch, silently ping near-but-stale drivers to refresh GPS, then ring the ones now in range. KEEP 0 until the new app is live — the old app shows a blank notification.' },
    { key: 'LOCATION_SWEEP_ENABLED', group: 'Location ping (probe & sweep)', label: 'Scheduled sweep — ON / OFF', defaultVal: '0', options: ['0', '1'], hint: 'REQUIRES the new app. 1 = every ~3h (daytime IST) silently refresh stale offline-driver locations before the next ride. KEEP 0 until the new app is live.' },
    { key: 'LOCATION_PROBE_RECHECK_DELAY_MS', group: 'Location ping (probe & sweep)', label: 'Probe recheck delay (ms)', defaultVal: '12000', hint: 'How long after the silent probe to re-check who refreshed and ring them.' },
    { key: 'LOCATION_PING_TTL_SECONDS', group: 'Location ping (probe & sweep)', label: 'Location ping FCM TTL (s)', defaultVal: '120', hint: 'Drop the silent ping if it cannot be delivered within this.' },
    { key: 'LOCATION_SWEEP_STALE_CEILING_MIN', group: 'Location ping (probe & sweep)', label: 'Sweep stale ceiling (min)', defaultVal: '4320', hint: 'The sweep pings drivers stale up to this many minutes (4320 = 72h). Older than this is skipped.' },
    { key: 'LOCATION_SWEEP_MAX_PER_CITY', group: 'Location ping (probe & sweep)', label: 'Sweep max per city', defaultVal: '200', hint: 'Cap on drivers pinged per city per sweep run.' },
    { key: 'LOC_PING_MAX_PER_DAY', group: 'Location ping (probe & sweep)', label: 'Max silent pings / driver / day', defaultVal: '2', hint: 'Per-driver daily cap on silent location pings.' },

    // --- Missed-rides digest (#9): daily "you missed rides near you" push (works on the current app) ---
    { key: 'MISSED_DIGEST_ENABLED', group: 'Missed-rides digest', label: 'Missed-rides digest — ON / OFF', defaultVal: '0', options: ['0', '1'], hint: '1 = daily 09:30 IST push to offline drivers who had rides pass near them. Works on the CURRENT app (the in-app card needs the new app). Honors quiet hours + "Not now".' },
    { key: 'MISSED_DIGEST_MIN_RIDES', group: 'Missed-rides digest', label: 'Min missed rides to notify', defaultVal: '2', hint: 'Only push drivers with at least this many missed nearby rides in the last 24h.' },
    { key: 'MISSED_DIGEST_MAX_PER_CITY', group: 'Missed-rides digest', label: 'Digest max per city', defaultVal: '300', hint: 'Cap on drivers evaluated per city per digest run.' },
    { key: 'DEMAND_SUMMARY_RADIUS_KM', group: 'Missed-rides digest', label: 'Missed-rides radius (km)', defaultVal: '2', hint: 'Radius for "rides that passed near you" — used by both the in-app card and the digest.' },

    // --- Streaks & bonuses (#10): tracking always runs; bonuses pay only when amount > 0 ---
    { key: 'STREAK_TARGET_DAYS', group: 'Streaks & bonuses', label: 'Streak target (days)', defaultVal: '5', hint: 'Consecutive online days for a streak milestone.' },
    { key: 'STREAK_BONUS_AMOUNT', group: 'Streaks & bonuses', label: 'Streak bonus (₹)', defaultVal: '0', hint: '0 = no payout (tracking + card still work). Set > 0 to credit the wallet at the milestone.' },
    { key: 'FIRST_RIDE_BONUS_ENABLED', group: 'Streaks & bonuses', label: 'First-ride bonus — ON / OFF', defaultVal: '0', options: ['0', '1'], hint: '1 = credit a bonus for the day’s first completed ride before the cutoff hour.' },
    { key: 'FIRST_RIDE_BONUS_AMOUNT', group: 'Streaks & bonuses', label: 'First-ride bonus (₹)', defaultVal: '0', hint: 'Wallet credit for the first completed ride of the day.' },
    { key: 'FIRST_RIDE_BONUS_BEFORE_HOUR', group: 'Streaks & bonuses', label: 'First-ride cutoff (IST hour)', defaultVal: '11', hint: 'Only rides completed before this IST hour (0–23) earn the first-ride bonus.' },

    // --- SMS re-engagement (#8): weekly SMS to FCM-dead drivers. Needs a DLT template + env ---
    { key: 'REENGAGE_SMS_ENABLED', group: 'SMS re-engagement', label: 'SMS re-engagement — ON / OFF', defaultVal: '0', options: ['0', '1'], hint: 'REQUIRES a DLT-approved MSG91 flow + MSG91_REENGAGE_FLOW_ID env. 1 = weekly SMS to FCM-dead drivers (the ~650 push can never reach). Targets ONLY tokenless drivers.' },
    { key: 'REENGAGE_SMS_MAX_PER_CITY', group: 'SMS re-engagement', label: 'SMS max per city / week', defaultVal: '200', hint: 'Cap on FCM-dead drivers SMSed per city per week.' },
    { key: 'REENGAGE_SMS_LINK', group: 'SMS re-engagement', label: 'SMS app link', defaultVal: 'https://apnayatri.app', hint: 'Short app link inserted into the re-engagement SMS.' },
];
// Two tabs so off-duty-wake tuning and demand-nudge tuning don't crowd each other.
const TUNING_TABS: { label: string; groups: string[] }[] = [
    { label: 'Offline Wake & Dispatch', groups: ['Reach & radius', 'Offline wake (off-duty drivers)', 'Advanced'] },
    { label: 'Demand Nudge', groups: ['Demand nudge'] },
    { label: 'Reach Boosters', groups: ['Re-dispatch waves', 'Location ping (probe & sweep)'] },
    { label: 'Engagement', groups: ['Missed-rides digest', 'Streaks & bonuses', 'SMS re-engagement'] },
];

// Offline-wake breakdown as plain-English points (§23). isPreview = "would happen" (Reach Preview, dry
// run); else = "what happened" (Dispatch Insights, actuals). Quiet hours is the headline case: it shows
// "0 woken now, but N would have rung if it weren't quiet".
const offlineWakePoints = (ow: OfflineWakeReport, isPreview: boolean): string[] => {
    const verb = ow.mode === 'loud' ? 'rung (full ride ring)' : 'nudged (soft banner)';
    const pts: string[] = [];
    pts.push(`Style: ${ow.mode === 'loud' ? 'LOUD — full ride ring, same as online drivers' : 'GENTLE — soft "go online" banner'}.`);
    pts.push(`${ow.nearTotal} offline driver(s) near the pickup.`);
    const stale = ow.staleTotal ?? 0;
    if (ow.recencyExcluded > 0) {
        const tooOld = Math.max(0, ow.recencyExcluded - stale);
        pts.push(
            `${ow.recencyExcluded} are past the ${ow.recencyMin ?? '?'}-min fresh gate (too stale for a loud ring)` +
            ((stale > 0 || tooOld > 0)
                ? ` — of those, ${stale} are recent enough for a gentle Tier-2.5 nudge and ${tooOld} too old to trust (skipped).`
                : '.')
        );
    }
    if (stale > 0) {
        pts.push(`Tier 2.5: ${stale} near-but-stale driver(s) ${isPreview ? 'would get' : 'got'} a gentle ride-context nudge${!isPreview && ow.staleNotified != null ? ` (${ow.staleNotified} sent)` : ''}.`);
    }
    pts.push(`${ow.candidates} passed the fresh gate → politeness check → ${ow.suppressedReject} blocked ("Not now"), ${ow.suppressedCap} hit the per-window cap.`);
    if (ow.quietHoursActive) {
        pts.push(`🌙 QUIET HOURS now → 0 offline drivers ${isPreview ? 'would be' : 'were'} woken. If it weren't quiet, ${ow.wouldNotify} would have been ${verb}.`);
    } else {
        pts.push(`${isPreview ? 'Would wake' : 'Woke'} ${isPreview ? ow.wouldNotify : ow.notified} offline driver(s) — ${verb}.`);
    }
    return pts;
};

const renderOfflineWakeAlert = (ow: OfflineWakeReport, isPreview: boolean) => (
    <Alert severity={ow.quietHoursActive ? 'warning' : 'info'} variant="outlined" sx={{ mt: 1.5 }}>
        <Typography variant="subtitle2" gutterBottom>
            Offline wake — {isPreview ? 'what would happen here' : 'what happened'}
        </Typography>
        <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { mb: 0.5 } }}>
            {offlineWakePoints(ow, isPreview).map((p, i) => <li key={i}>{p}</li>)}
        </Box>
    </Alert>
);

type CityOption = { label: string; value: string; };

const CityOperations: React.FC = () => {
    // --- STATE ---
    const [loading, setLoading] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [result, setResult] = useState<{ open: boolean, message: string, count: number } | null>(null);

    // Reference Data
    const [statesList, setStatesList] = useState<any[]>([]);
    const [citiesList, setCitiesList] = useState<any[]>([]);
    const [plansList, setPlansList] = useState<any[]>([]);
    const [selectedStateCode, setSelectedStateCode] = useState<string | null>(null);

    // Launch Mode + Force Online
    const [launchCities, setLaunchCities] = useState<string[]>([]);
    const [launchBusy, setLaunchBusy] = useState(false);
    const [forceBusy, setForceBusy] = useState(false);

    // Dispatch Insights
    const [reports, setReports] = useState<DispatchReport[]>([]);
    const [reportsBusy, setReportsBusy] = useState(false);
    // Dispatch Insights pagination (client-side — the report set is Redis-capped at 200/6h, so a
    // single fetch holds all rows; we just page through them here instead of rendering 50+ at once).
    const [reportsPage, setReportsPage] = useState(0);
    const [reportsPerPage, setReportsPerPage] = useState(10);

    // UI
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [expandedRide, setExpandedRide] = useState<number | null>(null);

    // Reach Preview (predictive)
    const [preview, setPreview] = useState<{ lat: string; lng: string; data: DispatchPreview | null; busy: boolean }>({
        lat: '', lng: '', data: null, busy: false,
    });

    // Dispatch Tuning (AppConfig)
    const [configs, setConfigs] = useState<AppConfigRow[]>([]);
    const [tuningDraft, setTuningDraft] = useState<Record<string, string>>({});
    const [tuningTab, setTuningTab] = useState(0);
    const [tuningSavingKey, setTuningSavingKey] = useState<string | null>(null);

    // #7 supply funnel + #11 gap zones (read-only, on-demand for the selected city).
    const [supplyFunnel, setSupplyFunnel] = useState<SupplyFunnel | null>(null);
    const [supplyGap, setSupplyGap] = useState<SupplyGapZones | null>(null);
    const [supplyBusy, setSupplyBusy] = useState(false);

    // A1 — Driver Reachability Inspector ("why isn't this driver getting rides?").
    const [inspect, setInspect] = useState<{ q: string; data: DriverReachability | null; busy: boolean; error: string | null }>({
        q: '', data: null, busy: false, error: null,
    });
    // B6 — Retrospective reach explainer (rideId + driver → which gate dropped them).
    const [explain, setExplain] = useState<{ rideId: string; q: string; data: ReachExplanation | null; busy: boolean; error: string | null }>({
        rideId: '', q: '', data: null, busy: false, error: null,
    });

    // TASK B — Historical Dispatch (durable DispatchLog, date-ranged, beyond the live 6h Redis window).
    const [history, setHistory] = useState<{
        rows: DispatchLogRow[]; summary: Record<string, number>; total: number;
        from: string; to: string; outcome: string; page: number; rowsPerPage: number;
        busy: boolean; loaded: boolean;
    }>({ rows: [], summary: {}, total: 0, from: '', to: '', outcome: '', page: 0, rowsPerPage: 25, busy: false, loaded: false });

    const loadHistory = async (opts?: { page?: number; rowsPerPage?: number }) => {
        const page = opts?.page ?? history.page;
        const rowsPerPage = opts?.rowsPerPage ?? history.rowsPerPage;
        setHistory(s => ({ ...s, busy: true }));
        try {
            const res = await getDispatchHistory({
                cityId: form.city || undefined,
                from: history.from || undefined,
                to: history.to || undefined,
                outcome: history.outcome || undefined,
                limit: rowsPerPage,
                offset: page * rowsPerPage,
            });
            setHistory(s => ({
                ...s, rows: res.data.rows || [], summary: res.data.summary || {}, total: res.data.total || 0,
                page, rowsPerPage, busy: false, loaded: true,
            }));
        } catch {
            setHistory(s => ({ ...s, busy: false, loaded: true }));
        }
    };

    const handleInspect = async () => {
        if (!inspect.q.trim()) return;
        setInspect(s => ({ ...s, busy: true, error: null }));
        try {
            const res = await getDriverReachability(inspect.q.trim());
            setInspect(s => ({ ...s, data: res.data, busy: false }));
        } catch (e: any) {
            setInspect(s => ({ ...s, data: null, busy: false, error: e.response?.data?.message || 'Lookup failed' }));
        }
    };

    const handleExplain = async () => {
        const rid = Number(explain.rideId);
        if (!rid || !explain.q.trim()) return;
        setExplain(s => ({ ...s, busy: true, error: null }));
        try {
            const res = await explainReach(rid, explain.q.trim());
            setExplain(s => ({ ...s, data: res.data, busy: false }));
        } catch (e: any) {
            setExplain(s => ({ ...s, data: null, busy: false, error: e.response?.data?.message || 'Explain failed' }));
        }
    };

    // Compact TTL formatter for the inspector chips: seconds → "3m 20s" / "2h 5m".
    const fmtTtl = (sec: number | null): string => {
        if (sec == null) return '—';
        if (sec < 60) return `${sec}s`;
        if (sec < 3600) return `${Math.floor(sec / 60)}m ${sec % 60}s`;
        return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
    };

    // Form State
    const [form, setForm] = useState({
        appType: 'ApnaYatri',
        city: '',
        planId: '',
        testPhoneNumber: ''
    });

    // --- INIT ---
    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        try {
            const [statesRes, plansRes, launchRes] = await Promise.all([
                getAllStates(),
                getSubscriptionPlans(),
                getLaunchMode()
            ]);
            if (statesRes.data.success) setStatesList(statesRes.data.states);
            // Handle both { data: [...] } or direct array based on your axios setup
            setPlansList(Array.isArray(plansRes.data) ? plansRes.data : (plansRes.data as any).data || []);
            if (launchRes.data?.success) setLaunchCities(launchRes.data.cities || []);
        } catch (e) { console.error("Failed to load init data", e); }
        loadTuning();
    };

    // --- DISPATCH TUNING (AppConfig read/write by key) ---
    const loadTuning = async () => {
        try {
            const res = await getAppConfigs();
            const rows: AppConfigRow[] = Array.isArray(res.data) ? res.data : ((res.data as any).data || []);
            setConfigs(rows);
            const draft: Record<string, string> = {};
            TUNING_KEYS.forEach(({ key, defaultVal }) => {
                const found = rows.find(r => r.key === key);
                draft[key] = found?.value ?? defaultVal;
            });
            setTuningDraft(draft);
        } catch (e) { console.error('Failed to load app configs', e); }
    };

    const saveTuning = async (key: string) => {
        const value = (tuningDraft[key] ?? '').trim();
        setTuningSavingKey(key);
        try {
            const existing = configs.find(c => c.key === key);
            if (existing) {
                await updateAppConfig(existing.id, { value });
            } else {
                await createAppConfig({ key, value, description: 'Dispatch tuning (set from City Operations)' });
            }
            await loadTuning();
        } catch (e: any) {
            alert(e.response?.data?.message || `Failed to save ${key}`);
        } finally {
            setTuningSavingKey(null);
        }
    };

    // --- DISPATCH INSIGHTS ---
    const loadReports = async () => {
        setReportsBusy(true);
        try {
            const res = await listDispatchReports(form.city || undefined, 50);
            if (res.data?.success) { setReports(res.data.reports || []); setReportsPage(0); }
        } catch (e) {
            console.error('Failed to load dispatch reports', e);
        } finally {
            setReportsBusy(false);
        }
    };

    useEffect(() => { loadReports(); /* initial */ }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // --- LAUNCH MODE ---
    const isCityLaunched = !!form.city && launchCities.includes(form.city);

    const handleToggleLaunch = async (enabled: boolean) => {
        if (!form.city) { alert('Select a Target City first.'); return; }
        setLaunchBusy(true);
        try {
            const res = await setLaunchMode(form.city, enabled);
            if (res.data?.success) setLaunchCities(res.data.cities || []);
        } catch (e: any) {
            alert(e.response?.data?.message || 'Failed to update Launch Mode');
        } finally { setLaunchBusy(false); }
    };

    const handleForceOnline = async () => {
        if (!form.testPhoneNumber && (!form.city || !form.appType)) {
            alert('Select App Type and City (or a Test Phone Number).');
            return;
        }
        setForceBusy(true);
        try {
            const res = await forceOnlineDrivers({
                city: form.city,
                appType: form.appType as 'JustZapIt' | 'ApnaYatri',
                testPhoneNumber: form.testPhoneNumber || undefined
            });
            setResult({
                open: true,
                message: res.data?.message || 'Done.',
                count: res.data?.forcedCount || 0
            });
        } catch (e: any) {
            setResult({ open: true, message: e.response?.data?.message || 'Failed to force online', count: 0 });
        } finally { setForceBusy(false); }
    };

    // --- REACH PREVIEW ---
    const handleCityChange = (cityName: string) => {
        handleChange('city', cityName);
        const c: any = citiesList.find(x => x.name === cityName);
        if (c && c.latitude && c.longitude) {
            // Auto-fill the preview pickup to the selected city's centre.
            setPreview(p => ({ ...p, lat: String(c.latitude), lng: String(c.longitude), data: null }));
        } else {
            setPreview(p => ({ ...p, data: null }));
        }
    };

    const handlePreview = async () => {
        const lat = parseFloat(preview.lat);
        const lng = parseFloat(preview.lng);
        if (isNaN(lat) || isNaN(lng)) {
            alert('Enter a valid pickup latitude & longitude (pick a city above to auto-fill it).');
            return;
        }
        setPreview(p => ({ ...p, busy: true }));
        try {
            const res = await getDispatchPreview(lat, lng);
            setPreview(p => ({ ...p, data: res.data, busy: false }));
        } catch (e: any) {
            alert(e.response?.data?.message || 'Failed to preview reach');
            setPreview(p => ({ ...p, busy: false }));
        }
    };

    const handleStateChange = async (stateCode: string | null) => {
        setSelectedStateCode(stateCode);
        setCitiesList([]);
        if (stateCode) {
            try {
                const res = await getCitiesByState(stateCode);
                if (res.data.success) setCitiesList(res.data.cities);
            } catch (e) { console.error(e); }
        }
    };

    const handleChange = (field: string, value: any) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    // --- ACTIONS ---
    const handleLaunchClick = () => {
        if (!form.planId) {
            alert("Please select a Subscription Plan to grant.");
            return;
        }
        if (!form.testPhoneNumber) {
            if (!form.appType || !form.city) {
                alert("Please select an App Type and City for production launch.");
                return;
            }
        }
        setConfirmOpen(true);
    };

    const handleConfirmLaunch = async () => {
        setConfirmOpen(false);
        setLoading(true);
        try {
            const response = await bulkActivateDrivers({
                city: form.city,
                planId: Number(form.planId),
                appType: form.appType as 'JustZapIt' | 'ApnaYatri',
                testPhoneNumber: form.testPhoneNumber || undefined
            });

            setResult({
                open: true,
                message: response.data?.message || 'Operation successful.',
                count: response.data?.activatedCount || 0
            });

            // Clear test number after success
            setForm(prev => ({ ...prev, testPhoneNumber: '' }));
        } catch (error: any) {
            console.error("Bulk activation failed", error);
            setResult({
                open: true,
                message: error.response?.data?.message || 'Failed to activate drivers',
                count: 0
            });
        } finally {
            setLoading(false);
        }
    };

    // --- HELPERS ---
    // #7 + #11: load the supply funnel + gap zones for the selected city. Prefer the exact cityId a
    // Reach Preview resolved (if any) over the display name, so counts match the dispatch string space.
    const handleLoadSupply = async (fresh = false) => {
        const city = preview.data?.cityId || form.city;
        if (!city) return;
        setSupplyBusy(true);
        try {
            const [f, g] = await Promise.all([getSupplyFunnel(city, fresh), getSupplyGapZones(city)]);
            setSupplyFunnel(f.data);
            setSupplyGap(g.data);
        } catch {
            // best-effort — leave any previously loaded data in place
        } finally {
            setSupplyBusy(false);
        }
    };

    // Live-config helpers for the Supply Health interpretation (reads the tuning values loaded on mount).
    const tuneNum = (k: string, d = '0') => parseFloat(tuningDraft[k] ?? d) || 0;
    // B4 — funnel trend delta suffix ("↑3" / "↓2") vs the week-ago snapshot; '' until history exists.
    const fmtDelta = (k: string): string => {
        const d = supplyFunnel?.trend?.deltas?.[k];
        if (d == null || d === 0) return '';
        return d > 0 ? ` ↑${d}` : ` ↓${Math.abs(d)}`;
    };
    const reachLever = (label: string, isOn: boolean, impact: string) => (
        <li key={label}>
            <Chip size="small" label={isOn ? 'ON' : 'OFF'} color={isOn ? 'success' : 'default'} variant={isOn ? 'filled' : 'outlined'}
                sx={{ height: 18, mr: 0.75, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }} />
            <strong>{label}</strong> — {impact}
        </li>
    );

    const cityOptions: CityOption[] = citiesList.map(c => ({ label: c.name, value: c.name }));
    let currentCityValue: CityOption | null = null;
    if (form.city) {
        currentCityValue = cityOptions.find(o => o.value === form.city) || { label: form.city, value: form.city };
    }
    const selectedPlanName = plansList.find(p => p.id === form.planId)?.name || 'Unknown Plan';
    const contextReady = !!form.city && !!form.appType;
    const scopeLabel = form.testPhoneNumber
        ? `Test phone ${form.testPhoneNumber}`
        : (contextReady ? `${form.city} · ${form.appType}` : 'No city selected');

    const sectionTitle = (icon: React.ReactNode, text: string, helpKey?: HelpKey) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <Typography variant="h6" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {icon} {text}
            </Typography>
            {helpKey && <PageHelp k={helpKey} />}
        </Box>
    );

    return (
        <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto', width: '100%' }}>
            {/* --- HEADER --- */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <BusinessCenter fontSize="large" color="primary" />
                    City Operations & Market Launch
                </Typography>
                <PageHelp k="cityOps" size="medium" />
            </Box>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                Pick your operating context, then run launch operations against it.
            </Typography>

            {/* --- 1 · OPERATING CONTEXT (shared settings) --- */}
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3 }}>
                {sectionTitle(<Tune />, '1 · Operating Context', 'cityOpsContext')}
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Choose who you're operating on. Everything below acts on this selection.
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                    <FormControl fullWidth>
                        <InputLabel>App Type</InputLabel>
                        <Select value={form.appType} label="App Type" onChange={(e) => handleChange('appType', e.target.value)}>
                            <MenuItem value="ApnaYatri">ApnaYatri</MenuItem>
                            <MenuItem value="JustZapIt">JustZapIt</MenuItem>
                        </Select>
                    </FormControl>

                    <Autocomplete
                        options={statesList}
                        getOptionLabel={(option) => option.name}
                        value={statesList.find(s => s.isoCode === selectedStateCode) || null}
                        onChange={(e, newValue) => handleStateChange(newValue ? newValue.isoCode : null)}
                        renderInput={(params) => <TextField {...params} label="State (filter)" />}
                    />

                    <Autocomplete
                        options={cityOptions}
                        getOptionLabel={(option) => option.label}
                        value={currentCityValue}
                        onChange={(e, newValue) => handleCityChange(newValue ? newValue.value : "")}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Target City"
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <>
                                            <InputAdornment position="start" sx={{ pl: 1 }}>
                                                <LocationOn fontSize="small" />
                                            </InputAdornment>
                                            {params.InputProps.startAdornment}
                                        </>
                                    )
                                }}
                            />
                        )}
                    />
                </Box>

                {/* Live scope summary */}
                <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    <Typography variant="caption" color="textSecondary">Operating on:</Typography>
                    <Chip
                        size="small"
                        icon={<LocationOn />}
                        label={scopeLabel}
                        color={contextReady || form.testPhoneNumber ? 'primary' : 'default'}
                        variant={contextReady || form.testPhoneNumber ? 'filled' : 'outlined'}
                    />
                    <Chip
                        size="small"
                        label={isCityLaunched ? 'Launch Mode: ON' : 'Launch Mode: OFF'}
                        color={isCityLaunched ? 'success' : 'default'}
                        variant={isCityLaunched ? 'filled' : 'outlined'}
                    />
                </Stack>

                {/* Advanced / testing override */}
                <Box sx={{ mt: 2 }}>
                    <Link component="button" type="button" underline="hover" variant="caption" onClick={() => setShowAdvanced(v => !v)}>
                        {showAdvanced ? 'Hide' : 'Show'} advanced / testing override
                    </Link>
                    <Collapse in={showAdvanced}>
                        <Box sx={{ mt: 1, p: 2, bgcolor: '#fff4e5', borderRadius: 2, border: '1px solid #ffcc80' }}>
                            <Typography variant="subtitle2" color="warning.dark" gutterBottom>
                                Testing override
                            </Typography>
                            <TextField
                                fullWidth
                                label="Test Phone Number"
                                placeholder="e.g. 9876543210"
                                value={form.testPhoneNumber}
                                onChange={(e) => handleChange('testPhoneNumber', e.target.value)}
                                helperText="If set, Force Online and Bulk Activation run ONLY for this phone (city & app type ignored)."
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><PhoneAndroid fontSize="small" /></InputAdornment>
                                }}
                            />
                        </Box>
                    </Collapse>
                </Box>
            </Paper>

            {/* --- 2 · LAUNCH MODE (primary launch switch) --- */}
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3 }}>
                {sectionTitle(<FlashOn />, '2 · Launch Mode (Guaranteed Reach)', 'cityOpsLaunchMode')}
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    When ON for a city, every ride request also wakes <strong>offline</strong> drivers near the pickup (FCM, by
                    last-known location), and any driver can accept without an active plan. This is the recommended launch
                    switch — instantly reversible, no database rows.
                </Typography>

                <FormControlLabel
                    control={
                        <Switch
                            checked={isCityLaunched}
                            disabled={!form.city || launchBusy}
                            onChange={(e) => handleToggleLaunch(e.target.checked)}
                        />
                    }
                    label={
                        form.city
                            ? `Launch Mode for "${form.city}" is ${isCityLaunched ? 'ON' : 'OFF'}`
                            : 'Select a Target City in Operating Context above to toggle'
                    }
                />

                <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="textSecondary">Active launch cities:</Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                        {launchCities.length === 0
                            ? <Chip size="small" label="None" variant="outlined" />
                            : launchCities.map(c => <Chip key={c} size="small" color="success" label={c} />)}
                    </Stack>
                </Box>
            </Paper>

            {/* --- SUPPLY HEALTH (#7 funnel + #11 gap zones) --- */}
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3 }}>
                {sectionTitle(<Insights />, 'Supply Health — Funnel & Gap Zones')}
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Why "registered" collapses to "reachable", and where rides chronically fail. Pick a city above, then load.
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={() => handleLoadSupply(false)}
                        disabled={(!form.city && !preview.data?.cityId) || supplyBusy}
                        endIcon={supplyBusy ? <CircularProgress size={18} color="inherit" /> : <Refresh />}
                        sx={{ borderRadius: 2 }}
                    >
                        {(preview.data?.cityId || form.city) ? `Load for ${preview.data?.cityId || form.city}` : 'Pick a city first'}
                    </Button>
                    {supplyFunnel?.asOf && (
                        <Chip
                            size="small"
                            variant="outlined"
                            label={supplyFunnel.source === 'snapshot' ? `As of ${supplyFunnel.asOf}` : 'Live (just computed)'}
                        />
                    )}
                    {supplyFunnel && (
                        // Default read is last night's snapshot row. This forces the six live scans over
                        // drivers-joined-users — deliberately an explicit click, never a page load.
                        <Button variant="text" size="small" onClick={() => handleLoadSupply(true)} disabled={supplyBusy}>
                            Recompute now
                        </Button>
                    )}
                </Stack>

                {supplyFunnel && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Supply funnel — {supplyFunnel.cityId}</Typography>
                        <Alert severity="info" variant="outlined" sx={{ mb: 1 }}>{supplyFunnel.note}</Alert>

                        {/* A3 — config lint: known-bad / notable reach-config combinations, warnings first. */}
                        {(supplyFunnel.lint || []).map((f, i) => (
                            <Alert key={i} severity={f.severity === 'warn' ? 'warning' : 'info'} variant="outlined" sx={{ mb: 1 }}>
                                <strong>{f.keys.join(' · ')}:</strong> {f.message}
                            </Alert>
                        ))}

                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                            <Chip size="small" label={`Registered: ${supplyFunnel.funnel.registered}${fmtDelta('registered')}`} />
                            <Chip size="small" color="primary" variant="outlined" label={`Live token: ${supplyFunnel.funnel.hasToken}${fmtDelta('hasToken')}`} />
                            <Chip size="small" color="primary" variant="outlined" label={`Token fresh 30d: ${supplyFunnel.funnel.tokenFresh30d}${fmtDelta('tokenFresh30d')}`} />
                            <Chip size="small" color="warning" variant="outlined" label={`Loc active 7d: ${supplyFunnel.funnel.locFresh7d}${fmtDelta('locFresh7d')}`} />
                            <Chip size="small" color="warning" variant="outlined" label={`Loc fresh 24h: ${supplyFunnel.funnel.locFresh24h}${fmtDelta('locFresh24h')}`} />
                            <Chip size="small" color="success" variant="outlined" label={`Accepted this week: ${supplyFunnel.funnel.acceptedThisWeek}${fmtDelta('acceptedThisWeek')}`} />
                        </Stack>
                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mt: 1 }}>
                            <Chip size="small" color="error" variant="outlined" label={`Dark to push (SMS-only): ${supplyFunnel.segments.tokenNull}${fmtDelta('tokenNull')}`} />
                            <Chip size="small" color="error" variant="outlined" label={`Dormant 7d+: ${supplyFunnel.segments.dormant7d}${fmtDelta('dormant7d')}`} />
                        </Stack>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                            {supplyFunnel.trend
                                ? `Deltas (↑/↓) vs the ${supplyFunnel.trend.since} snapshot — is each lever moving its stage?`
                                : 'Trend deltas appear once the daily funnel snapshot has ≥1 week of history.'}
                        </Typography>

                        {/* What each stage means + which lever moves it */}
                        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mt: 2 }}>What each number means / which lever moves it</Typography>
                        <Box component="ul" sx={{ pl: 2.5, m: 0, mt: 0.5, '& li': { mb: 0.5, fontSize: 13, color: 'text.secondary' } }}>
                            <li><strong>Registered → Live token</strong>: the {supplyFunnel.segments.tokenNull} with no token are <strong>dark to push</strong> — only <strong>SMS re-engagement </strong> can reach them.</li>
                            <li><strong>Token fresh 30d</strong>: (re)registered in 30d. Starts at 0 (the stamp is brand-new) and backfills as drivers open the app.</li>
                            <li><strong>Loc active 7d / fresh 24h</strong>: how many are actually dispatchable to a nearby ride. The <strong>Location probe + sweep</strong> keep this high between sessions.</li>
                            <li><strong>Dormant 7d+ ({supplyFunnel.segments.dormant7d})</strong>: have a token but idle — the <strong>Missed-rides digest</strong> and <strong>Streaks</strong> pull them back.</li>
                            <li><strong>Accepted this week</strong>: your truly-active core. Rides now tag <code>pickupCity</code> at creation, so this counts rides accepted from the fix onward (older rows stay untagged unless backfilled).</li>
                        </Box>

                        {/* Reach levers — live ON/OFF + impact (and what happens if you switch one off later) */}
                        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mt: 1.5 }}>Reach levers — current status &amp; impact</Typography>
                        <Box component="ul" sx={{ pl: 2.5, m: 0, mt: 0.5, '& li': { mb: 0.75, fontSize: 13, color: 'text.secondary', listStyle: 'none', ml: -2 } }}>
                            {reachLever('Tier 2.5 stale nudge', tuneNum('OFFLINE_WAKE_STALE_RECENCY_MIN') > 0, 'recovers near-but-stale drivers with a gentle nudge. Off → those drivers get nothing.')}
                            {reachLever('Re-dispatch waves', tuneNum('REDISPATCH_ENABLED') > 0, 'retries unaccepted rides at a widening radius. Off → a single burst only.')}
                            {reachLever('Location probe + sweep (needs new app)', tuneNum('LOCATION_PROBE_ENABLED') > 0 || tuneNum('LOCATION_SWEEP_ENABLED') > 0, 'silently refreshes stale GPS → grows "Loc fresh 24h/7d" and shrinks recency-skips. Off → those numbers only rise when drivers open the app themselves.')}
                            {reachLever('Missed-rides digest', tuneNum('MISSED_DIGEST_ENABLED') > 0, `re-activates the ${supplyFunnel.segments.dormant7d} dormant drivers with a daily push. Off → they stay dormant.`)}
                            {reachLever('Streaks / first-ride bonus', tuneNum('STREAK_BONUS_AMOUNT') > 0 || tuneNum('FIRST_RIDE_BONUS_ENABLED') > 0, 'pays a daily-online habit → grows Loc-active + Accepted-this-week. Off → no habit incentive (tracking still runs).')}
                            {reachLever('SMS re-engagement (needs DLT)', tuneNum('REENGAGE_SMS_ENABLED') > 0, `the ONLY lever for the ${supplyFunnel.segments.tokenNull} dark drivers. Off → they stay unreachable by anything.`)}
                        </Box>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                            Each lever moves a specific stage; switch one off later and that stage stops improving from it (organic app usage still counts). e.g. turning off the Location sweep lets "Loc fresh 24h" drift down → more recency-skips at dispatch.
                        </Typography>
                    </Box>
                )}

                {supplyGap && (
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Supply-gap zones — last {supplyGap.days}d, {supplyGap.totalRides} rides (failed = expired or reached &lt; {supplyGap.minNearReach})
                        </Typography>
                        {supplyGap.zones.length === 0 ? (
                            <Alert severity="success" variant="outlined">No logged rides yet — the heatmap fills as rides dispatch.</Alert>
                        ) : (
                            <Box sx={{ overflowX: 'auto' }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>#</TableCell>
                                            <TableCell>Cell (lat, lng)</TableCell>
                                            <TableCell align="right">Failed</TableCell>
                                            <TableCell align="right">Total</TableCell>
                                            <TableCell align="right">Expired</TableCell>
                                            <TableCell align="right">Low reach</TableCell>
                                            <TableCell>Peak hrs (IST)</TableCell>
                                            <TableCell>Fix lever</TableCell>
                                            <TableCell>Map</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {supplyGap.zones.map((z, i) => (
                                            <TableRow key={`${z.lat},${z.lng}`}>
                                                <TableCell>{i + 1}</TableCell>
                                                <TableCell>{z.lat.toFixed(3)}, {z.lng.toFixed(3)}</TableCell>
                                                <TableCell align="right"><strong>{z.failed}</strong></TableCell>
                                                <TableCell align="right">{z.total}</TableCell>
                                                <TableCell align="right">{z.expired ?? '—'}</TableCell>
                                                <TableCell align="right">{z.lowReach ?? '—'}</TableCell>
                                                <TableCell>{(z.peakHours || []).length ? (z.peakHours || []).map(h => `${h}:00`).join(', ') : '—'}</TableCell>
                                                <TableCell>
                                                    {/* B5 lever hint: wake-able stale supply → nudge/probe fixes it; 0 → recruit; -1 → not computed (tail cell). */}
                                                    {z.staleSupplyNearby < 0
                                                        ? <Typography variant="caption" color="textSecondary">—</Typography>
                                                        : z.staleSupplyNearby > 0
                                                            ? <Chip size="small" color="warning" variant="outlined" label={`Wake ${z.staleSupplyNearby} stale nearby`} />
                                                            : <Chip size="small" color="error" variant="outlined" label="Supply desert — recruit" />}
                                                </TableCell>
                                                <TableCell>
                                                    <Link href={`https://www.google.com/maps?q=${z.lat},${z.lng}`} target="_blank" rel="noopener">open</Link>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        )}
                    </Box>
                )}
            </Paper>

            {/* --- DRIVER REACHABILITY INSPECTOR (A1) + REACH EXPLAINER (B6) --- */}
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3 }}>
                {sectionTitle(<PhoneAndroid />, 'Driver Reachability — Inspector & Explainer')}
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    <strong>Inspector:</strong> "why isn't this driver getting rides?" — DB truth vs live presence (ghost
                    detection), token health, and every cooldown with its countdown. <strong>Explainer:</strong> pick a past
                    ride + a driver and see exactly which gate dropped them.
                </Typography>

                {/* A1 — Inspector */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        size="small" label="Driver phone or firebaseUid" value={inspect.q}
                        onChange={(e) => setInspect(s => ({ ...s, q: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleInspect(); }}
                        sx={{ minWidth: 280 }}
                    />
                    <Button
                        variant="outlined" onClick={handleInspect} disabled={!inspect.q.trim() || inspect.busy}
                        endIcon={inspect.busy ? <CircularProgress size={18} color="inherit" /> : <TravelExplore />}
                        sx={{ borderRadius: 2 }}
                    >
                        Inspect
                    </Button>
                </Box>
                {inspect.error && <Alert severity="error" variant="outlined" sx={{ mt: 2 }}>{inspect.error}</Alert>}
                {inspect.data && (
                    <Box sx={{ mt: 2 }}>
                        {/* Consistency flags — each one is a live-verified bug class. */}
                        {inspect.data.flags.length > 0
                            ? inspect.data.flags.map((f, i) => <Alert key={i} severity="warning" variant="outlined" sx={{ mb: 1 }}>{f}</Alert>)
                            : <Alert severity="success" variant="outlined" sx={{ mb: 1 }}>No consistency problems — DB, presence and token agree.</Alert>}

                        <Typography variant="subtitle2" sx={{ mt: 1.5 }}>
                            {inspect.data.user.name || 'Driver'} · {inspect.data.user.phone || inspect.data.user.uid}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mt: 1 }}>
                            <Chip size="small" label={`DB status: ${inspect.data.driver?.status ?? 'no driver row'}`}
                                color={inspect.data.driver?.status === 'ONLINE' ? 'success' : 'default'} />
                            <Chip size="small" variant="outlined"
                                label={`Redis presence: ${inspect.data.presence.redisStatus ?? 'none'}${inspect.data.presence.redisStatusTtlSec != null ? ` (${fmtTtl(inspect.data.presence.redisStatusTtlSec)})` : ''}`} />
                            <Chip size="small" variant="outlined"
                                color={inspect.data.presence.onDuty ? 'success' : 'default'}
                                label={`On-duty marker: ${inspect.data.presence.onDuty ? `yes (${fmtTtl(inspect.data.presence.onDutyTtlSec)})` : 'no'}`} />
                            <Chip size="small" variant="outlined"
                                color={inspect.data.token.present ? 'primary' : 'error'}
                                label={inspect.data.token.present ? `Token ok (${inspect.data.token.ageHours ?? '?'}h old)` : 'NO TOKEN'} />
                            <Chip size="small" variant="outlined"
                                label={`Durable loc: ${inspect.data.driver?.locAgeSec != null ? fmtTtl(inspect.data.driver.locAgeSec) + ' ago' : 'never'} · ${inspect.data.driver?.lastCityId ?? '?'}`} />
                            {inspect.data.presence.geoVsDurableKm != null && (
                                <Chip size="small" variant="outlined"
                                    color={inspect.data.presence.geoVsDurableKm > 0.5 ? 'warning' : 'default'}
                                    label={`GEO vs durable: ${inspect.data.presence.geoVsDurableKm} km apart`} />
                            )}
                            <Chip size="small" variant="outlined" label={`Streak: ${inspect.data.streak.count}d (last ${inspect.data.streak.lastDate ?? '—'})`} />
                        </Stack>

                        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mt: 2 }}>Cooldowns & caps (why a nudge/ring might be suppressed right now)</Typography>
                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                            <Chip size="small" variant="outlined"
                                color={inspect.data.cooldowns.offlineWakeBlocked ? 'error' : 'default'}
                                label={inspect.data.cooldowns.offlineWakeBlocked ? `"Not now" block (${fmtTtl(inspect.data.cooldowns.offlineWakeBlockTtlSec)})` : 'No "Not now" block'} />
                            <Chip size="small" variant="outlined"
                                label={`Wake count: ${inspect.data.cooldowns.offlineWakeCount}${inspect.data.cooldowns.offlineWakeCountTtlSec != null ? ` (resets ${fmtTtl(inspect.data.cooldowns.offlineWakeCountTtlSec)})` : ''}`} />
                            <Chip size="small" variant="outlined"
                                label={`Burst wake: ${inspect.data.cooldowns.burstWakeTtlSec != null ? `cooling ${fmtTtl(inspect.data.cooldowns.burstWakeTtlSec)}` : 'clear'}`} />
                            <Chip size="small" variant="outlined"
                                label={`Demand nudge: ${inspect.data.cooldowns.demandNudgeTtlSec != null ? `cooling ${fmtTtl(inspect.data.cooldowns.demandNudgeTtlSec)}` : 'clear'}`} />
                            <Chip size="small" variant="outlined"
                                label={`Silent pings today: ${inspect.data.cooldowns.locPingCount}${inspect.data.cooldowns.locPingTtlSec != null ? ` (resets ${fmtTtl(inspect.data.cooldowns.locPingTtlSec)})` : ''}`} />
                            {inspect.data.cooldowns.pendingHandoffRideId != null && (
                                <Chip size="small" color="success" variant="outlined"
                                    label={`Handoff armed → ride #${inspect.data.cooldowns.pendingHandoffRideId} (${fmtTtl(inspect.data.cooldowns.pendingHandoffTtlSec)})`} />
                            )}
                        </Stack>
                    </Box>
                )}

                {/* B6 — Explainer */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mt: 3 }}>
                    <TextField
                        size="small" label="Ride ID" value={explain.rideId}
                        onChange={(e) => setExplain(s => ({ ...s, rideId: e.target.value }))}
                        sx={{ width: 120 }}
                    />
                    <TextField
                        size="small" label="Driver phone or firebaseUid" value={explain.q}
                        onChange={(e) => setExplain(s => ({ ...s, q: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleExplain(); }}
                        sx={{ minWidth: 280 }}
                    />
                    <Button
                        variant="outlined" onClick={handleExplain}
                        disabled={!Number(explain.rideId) || !explain.q.trim() || explain.busy}
                        endIcon={explain.busy ? <CircularProgress size={18} color="inherit" /> : <Insights />}
                        sx={{ borderRadius: 2 }}
                    >
                        Why didn't they get this ride?
                    </Button>
                </Box>
                {explain.error && <Alert severity="error" variant="outlined" sx={{ mt: 2 }}>{explain.error}</Alert>}
                {explain.data && (
                    <Box sx={{ mt: 2 }}>
                        <Alert severity={explain.data.gates.every(g => g.pass) ? 'success' : 'warning'} variant="outlined" sx={{ mb: 1 }}>
                            <strong>Verdict:</strong> {explain.data.verdict}
                        </Alert>
                        <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { mb: 0.5, fontSize: 13 } }}>
                            {explain.data.gates.map((g, i) => (
                                <li key={i} style={{ color: g.pass ? undefined : '#d32f2f' }}>
                                    {g.pass ? '✅' : '❌'} <strong>{g.gate}</strong> — {g.detail}
                                </li>
                            ))}
                        </Box>
                        {explain.data.caveats.length > 0 && (
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                                {explain.data.caveats.join(' ')}
                            </Typography>
                        )}
                    </Box>
                )}
            </Paper>

            {/* --- 3 · REACH PREVIEW (predictive — sends nothing) --- */}
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3 }}>
                {sectionTitle(<TravelExplore />, '3 · Reach Preview', 'cityOpsPreview')}
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Dry run — see who a ride request at this pickup <strong>would</strong> reach right now, without sending
                    anything. Picking a city above auto-fills the pickup to the city centre; edit it for a specific area
                    (e.g. Gomti Nagar). Only drivers within the dispatch radius are targeted — never the whole city.
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr auto' }, gap: 2, alignItems: 'start' }}>
                    <TextField
                        label="Pickup latitude"
                        value={preview.lat}
                        onChange={(e) => setPreview(p => ({ ...p, lat: e.target.value }))}
                        placeholder="e.g. 26.858879308171044"
                    />
                    <TextField
                        label="Pickup longitude"
                        value={preview.lng}
                        onChange={(e) => setPreview(p => ({ ...p, lng: e.target.value }))}
                        placeholder="e.g. 80.99627553452493"
                    />
                    <Button
                        variant="contained"
                        onClick={handlePreview}
                        disabled={preview.busy || !preview.lat || !preview.lng}
                        endIcon={preview.busy ? <CircularProgress size={18} color="inherit" /> : <TravelExplore />}
                        sx={{ borderRadius: 2, height: 56 }}
                    >
                        {preview.busy ? 'Checking…' : 'Preview Reach'}
                    </Button>
                </Box>

                {preview.data && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            A ride here would reach{' '}
                            <Box component="span" sx={{ color: 'info.main', fontWeight: 800 }}>{preview.data.totalReach}</Box>
                            {' '}driver{preview.data.totalReach === 1 ? '' : 's'}.
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 2 }}>
                            <Chip size="small" label={`City: ${preview.data.cityId}`} />
                            <Chip
                                size="small"
                                color={preview.data.launchModeOn ? 'success' : 'default'}
                                variant={preview.data.launchModeOn ? 'filled' : 'outlined'}
                                label={preview.data.launchModeOn ? 'Launch Mode: ON' : 'Launch Mode: OFF'}
                            />
                            <Chip size="small" variant="outlined" color="success" label={`Live (≤${preview.data.tier1.radiusKm}km): ${preview.data.tier1.count}`} />
                            <Chip
                                size="small"
                                variant="outlined"
                                color="warning"
                                label={preview.data.tier2.enabled
                                    ? `Offline woken (≤${preview.data.tier2.radiusKm}km, cap ${preview.data.tier2.maxResults}): ${preview.data.tier2.count}`
                                    : 'Offline: off (enable Launch Mode)'}
                            />
                            {preview.data.tier3 && (
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    color={preview.data.tier3.enabled ? 'error' : 'default'}
                                    label={preview.data.tier3.enabled
                                        ? `Tier-3 city-wide wake: ${preview.data.tier3.count} (ON — usually noise)`
                                        : 'Tier-3 city-wide wake: OFF'}
                                />
                            )}
                            {preview.data.demandNudge && (
                                <Chip
                                    size="small"
                                    icon={<Campaign />}
                                    color={preview.data.demandNudge.wouldFire ? 'secondary' : 'default'}
                                    variant={preview.data.demandNudge.wouldFire ? 'filled' : 'outlined'}
                                    label={preview.data.demandNudge.wouldFire
                                        ? `Demand Nudge → ${preview.data.demandNudge.audience} offline driver(s)`
                                        : 'Demand Nudge: would not fire'}
                                />
                            )}
                        </Stack>

                        {!preview.data.launchModeOn && (
                            <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                                Launch Mode is OFF for this city — only live (online) drivers are reached. Turn it ON above to
                                also wake nearby offline drivers.
                            </Alert>
                        )}

                        {/* What a REAL ride at this pickup would do — preview is a dry run, this spells out live behaviour. */}
                        <Alert severity="success" variant="outlined" icon={<Info />} sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>What happens on a real ride here</Typography>
                            <Box component="ol" sx={{ pl: 2.5, m: 0, '& li': { mb: 0.5 } }}>
                                <li><strong>Tier 1</strong> — {preview.data.tier1.count} live driver(s) within {preview.data.tier1.radiusKm} km get a ride card instantly.</li>
                                <li><strong>Tier 2</strong> — {preview.data.tier2.enabled ? `${preview.data.tier2.count} offline driver(s) near the pickup are woken by FCM with the same ride card.` : 'OFF (Launch Mode off) — no offline wake.'}</li>
                                <li>
                                    <strong>Demand Nudge</strong> —{' '}
                                    {preview.data.demandNudge
                                        ? preview.data.demandNudge.note
                                        : '(redeploy backend to preview)'}
                                    . This is a <em>separate</em> message ("go online to catch rides"), <strong>no ride card, no Accept</strong>.
                                </li>
                            </Box>
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                                This panel sends nothing and consumes no cooldown. A live ride may nudge <em>fewer</em> drivers than shown
                                — anyone nudged in the last {tuningDraft['DEMAND_NUDGE_COOLDOWN_HOURS'] || '3'} h is skipped.
                                {' '}Drivers within <strong>{tuningDraft['DEMAND_NUDGE_NEAR_KM'] || '4'} km</strong> of the pickup get personalized
                                distance/fare/area copy (farther get the generic "demand is high"); a near driver who then goes online is handed the actual ride card.
                            </Typography>
                        </Alert>

                        {/* Offline-wake projection — crucially shows what is suppressed RIGHT NOW (quiet hours / recency / cap). */}
                        {preview.data.offlineWake && preview.data.launchModeOn && renderOfflineWakeAlert(preview.data.offlineWake, true)}

                        {!preview.data.drivers ? (
                            <Alert severity="info" variant="outlined">
                                Reached <strong>{preview.data.totalReach}</strong> driver(s). The per-driver list needs the
                                latest backend — redeploy the backend to see names &amp; distances here.
                            </Alert>
                        ) : preview.data.drivers.length === 0 ? (
                            <Alert severity="warning" variant="outlined">
                                No drivers in range. (A driver must have been online at least once to have a known location.)
                            </Alert>
                        ) : (
                            <Box sx={{ overflowX: 'auto' }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Nearest drivers it would reach (max 25)</Typography>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>#</TableCell>
                                            <TableCell>Driver</TableCell>
                                            <TableCell>Phone</TableCell>
                                            <TableCell>Reach</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell align="right">Distance from pickup</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {preview.data.drivers.map((d, i) => (
                                            <TableRow key={d.uid}>
                                                <TableCell>{i + 1}</TableCell>
                                                <TableCell>{d.name || d.phone || d.uid.slice(0, 8)}</TableCell>
                                                <TableCell>{d.phone || '—'}</TableCell>
                                                <TableCell>
                                                    <Chip size="small" variant="outlined" color={d.tier === 'live' ? 'success' : 'warning'} label={d.tier === 'live' ? 'Live' : 'Woken'} />
                                                </TableCell>
                                                <TableCell>{d.status}</TableCell>
                                                <TableCell align="right">{d.distanceKm != null ? `${d.distanceKm} km` : '—'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        )}
                    </Box>
                )}
            </Paper>

            {/* --- ACTIONS (act on the selected context) --- */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
                {/* Force All Online */}
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, display: 'flex', flexDirection: 'column' }}>
                    {sectionTitle(<PowerSettingsNew />, 'Force All Online', 'cityOpsForceOnline')}
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2, flex: 1 }}>
                        Sets all matching drivers to ONLINE and sends a wake notification (no plan granted). Best-effort — a
                        force-stopped or OEM-blocked phone may not wake. Acts on <strong>{scopeLabel}</strong>.
                    </Typography>
                    <Button
                        variant="outlined"
                        color="warning"
                        onClick={handleForceOnline}
                        disabled={forceBusy || (!form.testPhoneNumber && !contextReady)}
                        endIcon={forceBusy ? <CircularProgress size={18} color="inherit" /> : <PowerSettingsNew />}
                        sx={{ borderRadius: 2, alignSelf: 'flex-start' }}
                    >
                        {forceBusy ? 'Pushing wake-ups…' : 'Force All Online + Wake'}
                    </Button>
                </Paper>

                {/* Bulk Driver Activation */}
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, display: 'flex', flexDirection: 'column' }}>
                    {sectionTitle(<PlayArrow />, 'Bulk Driver Activation', 'cityOpsBulk')}
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Grants an <strong>actual subscription plan</strong> to all drivers, sets ONLINE + FCM. Heavier &amp;
                        persistent — use only when you want real plan rows (e.g. a plan with ride caps). Otherwise prefer
                        Launch Mode above. Acts on <strong>{scopeLabel}</strong>.
                    </Typography>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Grant Plan</InputLabel>
                        <Select value={form.planId} label="Grant Plan" onChange={(e) => handleChange('planId', e.target.value)}>
                            {plansList.map(plan => (
                                <MenuItem key={plan.id} value={plan.id}>
                                    {plan.name} (ID: {plan.id} - {plan.interval})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        onClick={handleLaunchClick}
                        disabled={loading || !form.planId}
                        endIcon={loading ? <CircularProgress size={18} color="inherit" /> : <PlayArrow />}
                        sx={{ borderRadius: 2, alignSelf: 'flex-start', mt: 'auto' }}
                    >
                        {loading ? 'Processing…' : 'Grant Plan to Drivers'}
                    </Button>
                </Paper>
            </Box>

            {/* --- 4 · DISPATCH INSIGHTS (per-ride reach) --- */}
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    {sectionTitle(<Insights />, '4 · Dispatch Insights — who each request reached', 'cityOpsInsights')}
                    <Tooltip title="Refresh">
                        <span>
                            <IconButton onClick={loadReports} disabled={reportsBusy}>
                                {reportsBusy ? <CircularProgress size={20} /> : <Refresh />}
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Recent ride requests and how many drivers each reached, {form.city ? <>filtered to <strong>{form.city}</strong> (the city selected above)</> : 'across all cities'}.
                    {' '}<strong>Reached</strong> = drivers targeted (live socket + offline FCM wake). <strong>Live</strong> = had a
                    connection; <strong>Woken</strong> = reached only by FCM (app killed/backgrounded/offline — the OS can't tell
                    which). <strong>FCM ok/fail</strong> = pushes accepted by Firebase (≠ guaranteed on-screen).
                    {' '}<strong>Demand nudge</strong> = if this ride hit a thin near-market, how many OFFLINE city drivers got a
                    separate "go online" ping (no ride card). Expand a row for the full <strong>offline-wake breakdown</strong>
                    (near, recency-skipped, Tier-2.5 gentle nudges, politeness suppressions). Change the city above and hit refresh to re-filter.
                </Typography>

                {reports.length === 0 ? (
                    <Alert severity="info" variant="outlined">
                        No recent dispatches{form.city ? ` for ${form.city}` : ''}. Trigger a ride request, then Refresh.
                    </Alert>
                ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Ride</TableCell>
                                    <TableCell>City</TableCell>
                                    <TableCell align="right">Reached</TableCell>
                                    <TableCell align="right">Live</TableCell>
                                    <TableCell align="right">Woken</TableCell>
                                    <TableCell align="right">On/Sil/Off</TableCell>
                                    <TableCell align="right">FCM ok/fail</TableCell>
                                    <TableCell align="center">Demand nudge</TableCell>
                                    <TableCell align="center">Accepted</TableCell>
                                    <TableCell align="right">Rejected</TableCell>
                                    <TableCell align="right">No reaction</TableCell>
                                    <TableCell>When</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {reports
                                    .slice(reportsPage * reportsPerPage, reportsPage * reportsPerPage + reportsPerPage)
                                    .map((r) => (
                                    <React.Fragment key={r.rideId}>
                                        <TableRow hover>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Tooltip title="Show reached drivers (nearest first)">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setExpandedRide(expandedRide === r.rideId ? null : r.rideId)}
                                                        >
                                                            {expandedRide === r.rideId ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                                                        </IconButton>
                                                    </Tooltip>
                                                    #{r.rideId}
                                                </Box>
                                            </TableCell>
                                            <TableCell>{r.cityId || '—'}</TableCell>
                                            <TableCell align="right"><strong>{r.reached}</strong></TableCell>
                                            <TableCell align="right">{r.liveConnected}</TableCell>
                                            <TableCell align="right">{r.wokenViaFcm}</TableCell>
                                            <TableCell align="right">{r.status.online}/{r.status.silent}/{r.status.offline}</TableCell>
                                            <TableCell align="right">{r.fcm.accepted}/{r.fcm.failed}</TableCell>
                                            <TableCell align="center">
                                                {r.demandNudge?.fired
                                                    ? <Tooltip title={`Thin near-market → pinged ${r.demandNudge.sent} of ${r.demandNudge.candidates} offline city driver(s) to go online`}>
                                                        <Chip size="small" color="secondary" icon={<Campaign />} label={r.demandNudge.sent} />
                                                      </Tooltip>
                                                    : <Chip size="small" variant="outlined" label="—" />}
                                            </TableCell>
                                            <TableCell align="center">
                                                {r.accepted.count
                                                    ? <Tooltip title={[r.accepted.byName, r.accepted.byPhone].filter(Boolean).join(' · ') || 'Accepted'}>
                                                        <Chip size="small" color="success" label={r.accepted.byName || r.accepted.byPhone || 'Yes'} />
                                                      </Tooltip>
                                                    : <Chip size="small" variant="outlined" label="No" />}
                                            </TableCell>
                                            <TableCell align="right">{r.rejected}</TableCell>
                                            <TableCell align="right">{r.ignored}</TableCell>
                                            <TableCell>{r.createdAt ? new Date(r.createdAt).toLocaleTimeString() : '—'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell colSpan={12} sx={{ py: 0, border: expandedRide === r.rideId ? undefined : 'none' }}>
                                                <Collapse in={expandedRide === r.rideId} unmountOnExit>
                                                    <Box sx={{ m: 1.5 }}>
                                                        {/* Offline-wake effect for THIS ride (§23): what happened + what quiet/recency/cap suppressed. */}
                                                        {r.offlineWake
                                                            ? renderOfflineWakeAlert(r.offlineWake, false)
                                                            : <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                                                                Offline-wake breakdown not recorded for this ride (predates the §23 backend redeploy).
                                                              </Typography>}

                                                        {/* A2 — post-dispatch TIMELINE: waves / probe rechecks / handoffs (was pm2-logs-only). */}
                                                        {(r.events && r.events.length > 0) && (
                                                            <Box sx={{ mt: 1.5 }}>
                                                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Timeline — after the initial dispatch</Typography>
                                                                <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { mb: 0.5, fontSize: 13, color: 'text.secondary' } }}>
                                                                    {r.events.map((ev, i) => {
                                                                        const at = new Date(ev.t).toLocaleTimeString();
                                                                        let text = '';
                                                                        if (ev.event === 'wave') {
                                                                            const added = Number(ev.live || 0) + Number(ev.offline || 0);
                                                                            text = added > 0
                                                                                ? `Re-dispatch wave ${ev.wave} (${ev.radiusKm} km) → +${ev.live} live, +${ev.offline} offline (fcmOk=${ev.fcmOk})`
                                                                                : `Re-dispatch wave ${ev.wave} (${ev.radiusKm} km) — no NEW drivers to reach`;
                                                                        } else if (ev.event === 'probe_sent') {
                                                                            text = `Silent location probe → ${ev.count} near-but-stale driver(s); recheck in ${Number(ev.recheckMs) / 1000}s`;
                                                                        } else if (ev.event === 'probe_recheck') {
                                                                            text = Number(ev.rung) > 0
                                                                                ? `Probe recheck: ${ev.nowFresh}/${ev.probed} refreshed near → rung ${ev.rung} (fcmOk=${ev.fcmOk})`
                                                                                : `Probe recheck: 0/${ev.probed} refreshed near in time — nobody rung`;
                                                                        } else if (ev.event === 'handoff') {
                                                                            text = `Nudge→card handoff: driver ${String(ev.driver).slice(0, 10)}… went online → rung this ride's card`;
                                                                        } else {
                                                                            text = `${ev.event}: ${JSON.stringify({ ...ev, t: undefined, event: undefined })}`;
                                                                        }
                                                                        return <li key={i}><strong>{at}</strong> — {text}</li>;
                                                                    })}
                                                                </Box>
                                                            </Box>
                                                        )}

                                                        <Typography variant="subtitle2" sx={{ mb: 1, mt: 1.5 }}>
                                                            Reached drivers — nearest first (max 25) · who accepted / rejected / didn't react
                                                        </Typography>
                                                        {(!r.drivers || r.drivers.length === 0) ? (
                                                            <Typography variant="body2" color="textSecondary">
                                                                No per-driver detail for this ride (older report, or no drivers reached).
                                                            </Typography>
                                                        ) : (
                                                            <Table size="small">
                                                                <TableHead>
                                                                    <TableRow>
                                                                        <TableCell>#</TableCell>
                                                                        <TableCell>Driver</TableCell>
                                                                        <TableCell>Phone</TableCell>
                                                                        <TableCell>Reach</TableCell>
                                                                        <TableCell>Status</TableCell>
                                                                        <TableCell>Reaction</TableCell>
                                                                        <TableCell align="right">Distance from rider</TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {r.drivers.map((d, i) => {
                                                                        const reaction = d.reaction === 'accepted'
                                                                            ? { color: 'success' as const, label: 'Accepted', filled: true }
                                                                            : d.reaction === 'rejected'
                                                                                ? { color: 'error' as const, label: 'Rejected', filled: false }
                                                                                : { color: 'warning' as const, label: 'No reaction', filled: false };
                                                                        return (
                                                                            <TableRow key={d.uid}>
                                                                                <TableCell>{i + 1}</TableCell>
                                                                                <TableCell>{d.name || d.phone || d.uid.slice(0, 8)}</TableCell>
                                                                                <TableCell>{d.phone || '—'}</TableCell>
                                                                                <TableCell>
                                                                                    <Chip
                                                                                        size="small"
                                                                                        variant="outlined"
                                                                                        color={d.tier === 'live' ? 'success' : 'warning'}
                                                                                        label={d.tier === 'live' ? 'Live' : 'Woken'}
                                                                                    />
                                                                                </TableCell>
                                                                                <TableCell>{d.status}</TableCell>
                                                                                <TableCell>
                                                                                    <Chip
                                                                                        size="small"
                                                                                        color={reaction.color}
                                                                                        variant={reaction.filled ? 'filled' : 'outlined'}
                                                                                        label={reaction.label}
                                                                                    />
                                                                                </TableCell>
                                                                                <TableCell align="right">{d.distanceKm != null ? `${d.distanceKm} km` : '—'}</TableCell>
                                                                            </TableRow>
                                                                        );
                                                                    })}
                                                                </TableBody>
                                                            </Table>
                                                        )}
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={reports.length}
                            page={reportsPage}
                            onPageChange={(_e, p) => { setReportsPage(p); setExpandedRide(null); }}
                            rowsPerPage={reportsPerPage}
                            onRowsPerPageChange={(e) => { setReportsPerPage(parseInt(e.target.value, 10)); setReportsPage(0); setExpandedRide(null); }}
                            rowsPerPageOptions={[10, 25, 50]}
                        />
                    </Box>
                )}
            </Paper>

            {/* --- DISPATCH HISTORY (durable DispatchLog — beyond the live 6h window) --- */}
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    {sectionTitle(<Insights />, 'Dispatch History (durable)')}
                    <Tooltip title="Load / refresh">
                        <span>
                            <IconButton onClick={() => loadHistory()} disabled={history.busy}>
                                {history.busy ? <CircularProgress size={20} /> : <Refresh />}
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Past dispatches beyond the live 6h window (compact — <strong>no per-driver reactions</strong>; those live
                    only in the expired Redis hash). Sourced from the durable <code>DispatchLog</code>,
                    {' '}{form.city ? <>filtered to <strong>{form.city}</strong> (the city selected above)</> : 'across all cities'}.
                    {' '}Pick a date range + outcome and load.
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr) auto auto' }, gap: 2, alignItems: 'center', mb: 2 }}>
                    <TextField
                        label="From" type="date" size="small" InputLabelProps={{ shrink: true }}
                        value={history.from} onChange={(e) => setHistory(s => ({ ...s, from: e.target.value }))}
                    />
                    <TextField
                        label="To" type="date" size="small" InputLabelProps={{ shrink: true }}
                        value={history.to} onChange={(e) => setHistory(s => ({ ...s, to: e.target.value }))}
                    />
                    <TextField
                        select size="small" label="Outcome" sx={{ minWidth: 150 }}
                        value={history.outcome} onChange={(e) => setHistory(s => ({ ...s, outcome: e.target.value }))}
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="accepted">Accepted</MenuItem>
                        <MenuItem value="expired">Expired</MenuItem>
                        <MenuItem value="cancelled">Cancelled</MenuItem>
                    </TextField>
                    <Button
                        variant="outlined" onClick={() => loadHistory({ page: 0 })} disabled={history.busy}
                        endIcon={history.busy ? <CircularProgress size={18} color="inherit" /> : <Insights />}
                        sx={{ borderRadius: 2 }}
                    >
                        Load
                    </Button>
                </Box>

                {history.loaded && (
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        <Chip size="small" variant="outlined" label={`Total: ${history.total}`} />
                        {Object.entries(history.summary).map(([outcome, count]) => (
                            <Chip
                                key={outcome} size="small"
                                color={outcome === 'accepted' ? 'success' : outcome === 'expired' ? 'error' : outcome === 'cancelled' ? 'warning' : 'default'}
                                variant={outcome === 'pending' ? 'outlined' : 'filled'}
                                label={`${outcome}: ${count}`}
                            />
                        ))}
                    </Stack>
                )}

                {history.loaded && history.rows.length === 0 ? (
                    <Alert severity="info" variant="outlined">
                        No durable dispatch rows{form.city ? ` for ${form.city}` : ''} in this range.
                    </Alert>
                ) : history.rows.length > 0 ? (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Ride</TableCell>
                                    <TableCell>City</TableCell>
                                    <TableCell align="right">Reached</TableCell>
                                    <TableCell align="right">Tier1/Tier2</TableCell>
                                    <TableCell align="right">Recency excl.</TableCell>
                                    <TableCell align="right">Stale</TableCell>
                                    <TableCell align="center">Nudge</TableCell>
                                    <TableCell>Outcome</TableCell>
                                    <TableCell>Dispatched</TableCell>
                                    <TableCell>Resolved</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {history.rows.map((r) => (
                                    <TableRow key={r.rideId} hover>
                                        <TableCell>#{r.rideId}</TableCell>
                                        <TableCell>{r.cityId || '—'}</TableCell>
                                        <TableCell align="right"><strong>{r.reached}</strong></TableCell>
                                        <TableCell align="right">{r.tier1Count}/{r.tier2Count}</TableCell>
                                        <TableCell align="right">{r.recencyExcluded}</TableCell>
                                        <TableCell align="right">{r.staleTotal}</TableCell>
                                        <TableCell align="center">{r.nudgeFired ? '✓' : '—'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                color={r.outcome === 'accepted' ? 'success' : r.outcome === 'expired' ? 'error' : r.outcome === 'cancelled' ? 'warning' : 'default'}
                                                variant={r.outcome === 'pending' ? 'outlined' : 'filled'}
                                                label={r.outcome}
                                            />
                                        </TableCell>
                                        <TableCell>{r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}</TableCell>
                                        <TableCell>{r.resolvedAt ? new Date(r.resolvedAt).toLocaleString() : '—'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={history.total}
                            page={history.page}
                            onPageChange={(_e, p) => loadHistory({ page: p })}
                            rowsPerPage={history.rowsPerPage}
                            onRowsPerPageChange={(e) => loadHistory({ page: 0, rowsPerPage: parseInt(e.target.value, 10) })}
                            rowsPerPageOptions={[25, 50, 100]}
                        />
                    </Box>
                ) : null}
            </Paper>

            {/* --- 5 · DISPATCH TUNING (AppConfig) --- */}
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mt: 3 }}>
                {sectionTitle(<Campaign />, '5 · Dispatch Tuning & Demand Nudge', 'cityOpsTuning')}
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    The knobs behind guaranteed reach + the honest demand nudge. Values are global, but the offline-wake and
                    demand-nudge behaviours only <strong>take effect in the Launch-Mode (guaranteed-reach) cities</strong> below.
                    Changes apply within ~5 min (config cache).
                </Typography>

                {/* Applicability tags — independent of the Operating Context city selection above. */}
                <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="caption" color="textSecondary">
                        Active in (Launch-Mode / guaranteed-reach cities):
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 0.75, flexWrap: 'wrap', gap: 1 }}>
                        {launchCities.length === 0
                            ? <Chip size="small" label="None — enable Launch Mode above" variant="outlined" color="warning" />
                            : launchCities.map(c => <Chip key={c} size="small" color="success" label={c} />)}
                    </Stack>
                </Box>

                <Tabs
                    value={tuningTab}
                    onChange={(_e, v) => setTuningTab(v)}
                    sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
                >
                    {TUNING_TABS.map((t, i) => <Tab key={t.label} label={t.label} value={i} />)}
                </Tabs>

                {TUNING_TABS[tuningTab].groups.map((group) => (
                    <Box key={group} sx={{ mb: 2.5 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 12 }}>
                            {group}
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                            {TUNING_KEYS.filter(k => k.group === group).map(({ key, label, defaultVal, hint, options }) => {
                                const exists = configs.some(c => c.key === key);
                                const draftVal = tuningDraft[key] ?? defaultVal;
                                const savedVal = configs.find(c => c.key === key)?.value ?? defaultVal;
                                const dirty = draftVal !== savedVal;
                                return (
                                    <Box key={key} sx={{ p: 2, border: '1px solid', borderColor: dirty ? 'secondary.main' : 'divider', borderRadius: 2 }}>
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                select={!!options}
                                                label={label}
                                                value={draftVal}
                                                onChange={(e) => setTuningDraft(p => ({ ...p, [key]: e.target.value }))}
                                                helperText={hint}
                                            >
                                                {options?.map(opt => (
                                                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                                ))}
                                            </TextField>
                                            <Button
                                                variant={dirty ? 'contained' : 'outlined'}
                                                color="secondary"
                                                size="small"
                                                onClick={() => saveTuning(key)}
                                                disabled={tuningSavingKey === key || !dirty}
                                                sx={{ minWidth: 84, mt: 0.5 }}
                                                startIcon={tuningSavingKey === key ? <CircularProgress size={14} color="inherit" /> : <Save />}
                                            >
                                                Save
                                            </Button>
                                        </Box>
                                        <Typography variant="caption" color="textSecondary">
                                            <code>{key}</code> · {exists ? `saved: ${savedVal}` : `not set — using default ${defaultVal}`}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                ))}
                {TUNING_TABS[tuningTab].label === 'Demand Nudge' && (
                    <NudgeTemplatesPanel launchCities={launchCities} />
                )}
            </Paper>

            {/* App-version adoption (#5 location_ping rollout gate) — decides when to raise
                LOCATION_PING_MIN_APP_VERSION. Scoped to the city picked above when one is set. */}
            <AppAdoptionPanel city={preview.data?.cityId || form.city} />

            {/* --- CONFIRMATION DIALOG (Bulk Activation) --- */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Warning color="warning" /> Confirm Bulk Plan Grant
                </DialogTitle>
                <DialogContent>
                    {form.testPhoneNumber ? (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <strong>TEST MODE:</strong> This will bypass the wallet and grant <strong>{selectedPlanName}</strong> to the driver with phone number <strong>{form.testPhoneNumber}</strong>.
                        </Alert>
                    ) : (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <strong>PRODUCTION MODE:</strong> You are about to bulk-grant <strong>{selectedPlanName}</strong> to ALL drivers in <strong>{form.city}</strong> for <strong>{form.appType}</strong>.
                            <br/><br/>They will be forced ONLINE and receive a push notification.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button onClick={handleConfirmLaunch} variant="contained" color="primary" autoFocus>
                        Confirm & Execute
                    </Button>
                </DialogActions>
            </Dialog>

            {/* --- RESULT DIALOG --- */}
            <Dialog open={!!result} onClose={() => setResult(null)}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {result?.count ? <CheckCircle color="success" /> : <Info color="error" />}
                    {result?.count ? 'Operation Successful' : 'Status Info'}
                </DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>{result?.message}</Typography>
                    {result?.count ? (
                        <Typography variant="h6" align="center" sx={{ mt: 2, color: 'success.main', fontWeight: 'bold' }}>
                            🚀 {result.count} drivers affected!
                        </Typography>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResult(null)} variant="contained">Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CityOperations;
